import React, { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { VERTICALS } from "../../data/kdm";
import Reveal from "../Reveal";

function ImageSlideshow({ images, alt }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % images.length), 3800);
    return () => clearInterval(id);
  }, [images.length]);

  return (
    <div className="relative w-full h-full">
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${
            i === idx ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      {images.length > 1 && (
        <div className="absolute bottom-4 right-4 flex gap-1.5 z-10">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={(e) => {
                e.preventDefault();
                setIdx(i);
              }}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60 hover:bg-white"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Verticals() {
  return (
    <section
      id="verticals"
      data-testid="verticals-section"
      className="bg-[--kdm-bg-alt] py-28 lg:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-20">
          <div className="max-w-2xl">
            <p className="overline">Six Verticals · One Vision</p>
            <h2
              data-testid="verticals-heading"
              className="font-serif font-medium text-4xl lg:text-6xl mt-6 leading-[1.02] tracking-tight"
            >
              The architecture of <span className="italic text-gold">a modern conglomerate.</span>
            </h2>
          </div>
          <p className="text-base text-[--kdm-ink-soft] max-w-md leading-relaxed">
            Each vertical operates with autonomy and accountability — yet draws strength
            from a shared global network of partners, capital and craftsmanship.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 auto-rows-[minmax(280px,auto)] gap-6 lg:gap-8">
          {VERTICALS.map((v, idx) => {
            // Layout pattern: featured wide cards alternating
            const span = [
              "lg:col-span-7 lg:row-span-2",
              "lg:col-span-5",
              "lg:col-span-5",
              "lg:col-span-7",
              "lg:col-span-7",
              "lg:col-span-5",
            ][idx];
            return (
              <Reveal
                key={v.id}
                delay={idx * 60}
                className={`${span} group`}
              >
                <article
                  data-testid={`vertical-card-${v.id}`}
                  className="card-soft h-full flex flex-col overflow-hidden"
                >
                  {/* Image */}
                  <div className="relative h-56 lg:h-64 overflow-hidden">
                    {v.images && v.images.length > 1 ? (
                      <ImageSlideshow images={v.images} alt={v.title} />
                    ) : (
                      <img
                        src={v.image}
                        alt={v.title}
                        className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      />
                    )}
                    <div className="absolute top-5 left-5 flex items-center gap-3 z-10">
                      <span className="bg-white/90 backdrop-blur-sm text-[10px] tracking-[0.3em] px-3 py-1.5 font-semibold text-[--kdm-ink]">
                        {v.number}
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-7 lg:p-9 flex-1 flex flex-col">
                    <h3 className="font-serif text-2xl lg:text-3xl tracking-tight text-[--kdm-ink] flex items-start justify-between gap-4">
                      {v.title}
                      <ArrowUpRight
                        size={22}
                        className="text-gold mt-1 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 shrink-0"
                      />
                    </h3>
                    <p className="text-sm text-[--kdm-ink-soft] mt-4 leading-relaxed">
                      {v.summary}
                    </p>

                    <ul className="mt-7 space-y-4 border-t border-[--kdm-line] pt-6">
                      {v.entities.map((e) => (
                        <li key={e.name} className="text-sm">
                          <p className="font-medium text-[--kdm-ink]">{e.name}</p>
                          <p className="text-[--kdm-ink-soft] mt-1.5 leading-relaxed">{e.text}</p>
                          {e.link && (
                            <a
                              href={e.link.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`vertical-link-${e.link.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                              className="inline-flex items-center gap-1.5 mt-2 text-xs tracking-[0.2em] uppercase font-semibold text-gold hover:text-[--kdm-ink] transition-colors duration-300"
                            >
                              {e.link.label}
                              <ArrowUpRight size={12} />
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
